import { useId, useState } from 'react';
import {
  PAYMENT_METHOD_LABEL,
  RECEIVED_METHODS,
  isReceivedMethod,
  labDayOf,
  paymentAmountRefusal,
  paymentDetailsRefusal,
  type ReceivedMethod,
} from '@/domain';
import type { CasePayment } from '@/application/ports/FinanceRepository';
import { useAddPayment, useEditPayment } from '@/application/usecases/useFinance';
import { labToday } from '@/shared/format/instant';
import { formatAmount } from '@/shared/format/money';
import { strings } from '@/shared/strings';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { modalStyles } from '@/ui/primitives/Modal';
import { sheetStyles } from '@/ui/primitives/sheetStyles';
import { toast } from '@/ui/primitives/toastStore';
import { formRefusal } from '../clients/clientRefusal';
import { useConfirmAction } from '../shell/useConfirmAction';
import { parseAmount } from './moneyForm';
import styles from './Finance.module.css';

const t = strings.finance;

/** The fields of the form, by the names the server refuses them under. */
const FIELDS = ['amount', 'method', 'reference', 'note', 'paidOn'] as const;
type Field = (typeof FIELDS)[number];

export interface PaymentSheetProps {
  jobOrderNo: string;
  /** What the case still owes. For a payment being put right, what it would owe without that payment. */
  balance: number;
  /** The doctor's credit, when the payment is to come from it. Left off for money received. */
  credit?: number;
  /** The payment being put right. Left off to take a new one. */
  payment?: CasePayment;
  onClose: () => void;
}

/**
 * One payment on a case: taken, taken from the doctor's credit, or put right.
 * The request of 8 October 2026 (fourth).
 *
 * The amount starts at all the case owes, because that is the usual payment,
 * and may be less: the case is then paid in part. It is never more than the
 * case owes, and from credit never more than the doctor has; both are said
 * under the amount before anything is sent, in the server's words. Money
 * received says how it came, its reference and the day it came, none of them
 * required; credit has no method of its own. Asked about before it is sent,
 * with what will still be owed.
 */
export function PaymentSheet({ jobOrderNo, balance, credit, payment, onClose }: PaymentSheetProps) {
  const add = useAddPayment();
  const edit = useEditPayment();
  const ask = useConfirmAction();
  const ids = { amount: useId(), method: useId(), reference: useId(), paidOn: useId(), note: useId() };

  const editing = payment !== undefined;
  const fromCredit = !editing && credit !== undefined;
  const today = labToday();

  const [amount, setAmount] = useState(
    String(editing ? payment.amount : fromCredit ? Math.min(balance, credit) : balance),
  );
  const [method, setMethod] = useState<ReceivedMethod | ''>(
    editing && isReceivedMethod(payment.method) ? payment.method : '',
  );
  const [reference, setReference] = useState(payment?.reference ?? '');
  const [note, setNote] = useState(payment?.note ?? '');
  const [paidOn, setPaidOn] = useState(editing ? labDayOf(payment.paidAt) : today);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const pending = add.isPending || edit.isPending;

  function change<Value>(set: (value: Value) => void, field: Field) {
    return (value: Value) => {
      set(value);
      setTouched(true);
      setMessage(null);
      setErrors((current) => ({ ...current, [field]: undefined }));
    };
  }

  function refused(failure: unknown): void {
    const refusal = formRefusal(failure, FIELDS);
    if (refusal === null) throw failure;
    setErrors(refusal.fields);
    setMessage(refusal.message);
  }

  function submit() {
    const sum = parseAmount(amount);
    const wrong =
      paymentAmountRefusal(sum, balance, fromCredit ? credit : null) ??
      (fromCredit
        ? null
        : paymentDetailsRefusal(
            { method: method === '' ? null : method, reference, note, paidOn },
            today,
          ));
    setMessage(null);
    if (wrong !== null) {
      setErrors({ [wrong.field]: wrong.message });
      return;
    }
    setErrors({});

    const taken = sum as number;
    const left = Math.round((balance - taken) * 100) / 100;
    const leftSaid = left > 0 ? formatAmount(left) : null;
    const details = {
      reference: reference.trim() === '' ? null : reference.trim(),
      note: note.trim() === '' ? null : note.trim(),
    };

    if (editing) {
      ask(
        strings.confirm.savePayment(jobOrderNo),
        () =>
          edit
            .mutateAsync({
              jobOrderNo,
              id: payment.id,
              edit: { amount: taken, method: method === '' ? null : method, ...details, paidOn },
            })
            .then(() => {
              toast(t.paymentSaved, { tone: 'success' });
              onClose();
            }, refused),
        { title: t.editPaymentTitle(jobOrderNo), confirmLabel: t.savePayment },
      );
      return;
    }

    ask(
      fromCredit
        ? strings.confirm.useCredit(jobOrderNo, formatAmount(taken), leftSaid)
        : strings.confirm.takePayment(jobOrderNo, formatAmount(taken), leftSaid),
      () =>
        add
          .mutateAsync({
            jobOrderNo,
            input: fromCredit
              ? { amount: taken, method: 'credit', reference: null, note: details.note }
              : { amount: taken, method: method === '' ? null : method, ...details, paidOn },
          })
          .then((summary) => {
            toast(
              summary.status === 'paid'
                ? t.nowPaid(jobOrderNo)
                : fromCredit
                  ? t.creditUsed(formatAmount(taken))
                  : t.paymentAdded(formatAmount(taken)),
              { tone: 'success' },
            );
            onClose();
          }, refused),
      {
        title: fromCredit ? t.creditTitle(jobOrderNo) : t.paymentTitle(jobOrderNo),
        confirmLabel: fromCredit ? t.useCreditAction : t.takePayment,
      },
    );
  }

  const error = (field: Field) => errors[field] ?? null;
  const dayError = error('paidOn');

  return (
    <BottomSheet
      title={
        editing ? t.editPaymentTitle(jobOrderNo) : fromCredit ? t.creditTitle(jobOrderNo) : t.paymentTitle(jobOrderNo)
      }
      lead={
        editing
          ? t.editPaymentLead
          : fromCredit
            ? t.creditLead(formatAmount(balance), formatAmount(credit))
            : t.paymentLead(formatAmount(balance))
      }
      closeLabel={strings.sheet.close}
      onClose={onClose}
      unsaved={
        touched && !pending
          ? { question: t.discard, keep: strings.sheet.keep, discard: strings.sheet.discard }
          : null
      }
      footer={
        <>
          {message !== null && (
            <p className={styles.message} role="alert">
              {message}
            </p>
          )}
          <div className={sheetStyles.actions}>
            <Button onClick={onClose} disabled={pending}>
              {t.cancel}
            </Button>
            <Button variant="primary" className={sheetStyles.primary} loading={pending} onClick={submit}>
              {editing ? t.savePayment : fromCredit ? t.useCreditAction : t.takePayment}
            </Button>
          </div>
        </>
      }
    >
      <div className={styles.form}>
        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor={ids.amount}>
            {t.amount}
          </label>
          <input
            id={ids.amount}
            className={modalStyles.input}
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={amount}
            aria-required="true"
            aria-invalid={error('amount') !== null || undefined}
            aria-describedby={error('amount') === null ? undefined : `${ids.amount}-error`}
            data-autofocus
            onChange={(event) => change(setAmount, 'amount')(event.target.value)}
          />
          {error('amount') !== null && (
            <p id={`${ids.amount}-error`} className={modalStyles.error}>
              {error('amount')}
            </p>
          )}
        </div>

        {!fromCredit && (
          <>
            <div className={styles.pair}>
              <div className={modalStyles.field}>
                <label className={modalStyles.label} htmlFor={ids.method}>
                  {t.method}
                </label>
                <select
                  id={ids.method}
                  className={modalStyles.select}
                  value={method}
                  aria-invalid={error('method') !== null || undefined}
                  aria-describedby={error('method') === null ? undefined : `${ids.method}-error`}
                  onChange={(event) => change(setMethod, 'method')(event.target.value as ReceivedMethod | '')}
                >
                  <option value="">{t.methodNone}</option>
                  {RECEIVED_METHODS.map((key) => (
                    <option key={key} value={key}>
                      {PAYMENT_METHOD_LABEL[key]}
                    </option>
                  ))}
                </select>
                {error('method') !== null && (
                  <p id={`${ids.method}-error`} className={modalStyles.error}>
                    {error('method')}
                  </p>
                )}
              </div>

              <div className={modalStyles.field}>
                <label className={modalStyles.label} htmlFor={ids.paidOn}>
                  {t.paidOnLabel}
                </label>
                <input
                  id={ids.paidOn}
                  className={modalStyles.input}
                  type="date"
                  max={today}
                  value={paidOn}
                  aria-invalid={dayError !== null || undefined}
                  aria-describedby={dayError === null ? undefined : `${ids.paidOn}-error`}
                  onChange={(event) => change(setPaidOn, 'paidOn')(event.target.value)}
                />
                {dayError !== null && (
                  <p id={`${ids.paidOn}-error`} className={modalStyles.error}>
                    {dayError}
                  </p>
                )}
              </div>
            </div>

            <div className={modalStyles.field}>
              <label className={modalStyles.label} htmlFor={ids.reference}>
                {t.reference}
              </label>
              <input
                id={ids.reference}
                className={modalStyles.input}
                value={reference}
                autoComplete="off"
                placeholder={t.referencePlaceholder}
                aria-invalid={error('reference') !== null || undefined}
                aria-describedby={error('reference') === null ? undefined : `${ids.reference}-error`}
                onChange={(event) => change(setReference, 'reference')(event.target.value)}
              />
              {error('reference') !== null && (
                <p id={`${ids.reference}-error`} className={modalStyles.error}>
                  {error('reference')}
                </p>
              )}
            </div>
          </>
        )}

        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor={ids.note}>
            {t.noteOptional}
          </label>
          <input
            id={ids.note}
            className={modalStyles.input}
            value={note}
            autoComplete="off"
            aria-invalid={error('note') !== null || undefined}
            aria-describedby={error('note') === null ? undefined : `${ids.note}-error`}
            onChange={(event) => change(setNote, 'note')(event.target.value)}
          />
          {error('note') !== null && (
            <p id={`${ids.note}-error`} className={modalStyles.error}>
              {error('note')}
            </p>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}
